import React from "react";
import "./styles.css";

const Whitewash = () => {
  return (
    <div className="container">
      <h1>Whitewash Transactions: How Liquidity Tests, Resolutions, and Filings Prevent Abuse</h1>
      
      <p>
        A <strong>whitewash transaction</strong> occurs when a company provides financial assistance (e.g., loans or guarantees) to facilitate the purchase of its own shares, bypassing normal safeguards. To prevent abuse, jurisdictions require strict compliance with <strong>liquidity tests</strong>, <strong>shareholder/director approvals</strong>, and <strong>regulatory filings</strong>. These steps ensure transparency, protect creditors, and uphold corporate governance.
      </p>

      <h2>1. Liquidity Tests: Proving Solvency</h2>
      <p>
        Before approving financial assistance, companies must conduct a <strong>liquidity test</strong> (often called a <em>solvency declaration</em>) to prove they can:
      </p>
      <ul>
        <li><strong>Pay debts post-transaction:</strong> The company must remain solvent for 12+ months after providing assistance.</li>
        <li><strong>Maintain working capital:</strong> Assistance cannot jeopardize day-to-day operations.</li>
      </ul>
      <p>
        Directors typically sign a <strong>solvency resolution</strong>, attesting to these conditions under penalty of personal liability. Auditors or independent experts may validate the declaration.
      </p>

      <h2>2. Shareholder Approval: The Whitewash Resolution</h2>
      <p>
        Even if solvency is proven, most jurisdictions require <strong>shareholder approval</strong> via a special resolution (e.g., 75% majority vote). Key steps include:
      </p>
      <ul>
        <li><strong>Disclosure:</strong> Shareholders receive details of the assistance (amount, recipient, purpose).</li>
        <li><strong>No conflict of interest:</strong> Recipients (e.g., directors buying shares) cannot vote on the resolution.</li>
        <li><strong>Filing:</strong> The resolution must be filed with the <em>Companies House</em> (UK), <em>SEC</em> (US), or <em>Registrar of Companies</em> (AU) to ensure public record.</li>
      </ul>
      <p>
        This "whitewash resolution" acts as a safeguard against insider exploitation.
      </p>

      <h2>3. Director Resolutions: Acknowledging Responsibility</h2>
      <p>
        Directors must pass a <strong>board resolution</strong> confirming:
      </p>
      <ul>
        <li>The assistance is in the company’s best interests.</li>
        <li>They have reviewed liquidity tests and disclosed material risks.</li>
        <li>They accept potential <strong>personal liability</strong> for breaches (e.g., wrongful trading claims).</li>
      </ul>
      <p>
        This resolution is often filed alongside shareholder approvals to regulators.
      </p>

      <h2>4. Regulatory Filings: Public Transparency</h2>
      <p>
        To finalize a whitewash transaction, companies must file:
      </p>
      <ul>
        <li><strong>Solvency declarations</strong> (signed by directors).</li>
        <li><strong>Shareholder resolutions</strong> (with voting results).</li>
        <li><strong>Board resolutions</strong> (acknowledging compliance).</li>
      </ul>
      <p>
        These documents are submitted to the relevant authority (e.g., <em>Companies House</em> in the UK, <em>SEC</em> in the US, or <em>ASIC</em> in Australia) within strict deadlines (often 15–30 days post-approval).
      </p>

      <h2>Why This Process Matters</h2>
      <p>
        Without these safeguards, whitewash transactions could:
      </p>
      <ul>
        <li><strong>Artificially inflate share prices</strong> by masking true demand.</li>
        <li><strong>Expose companies to insolvency</strong> if assistance drains cash reserves.</li>
        <li><strong>Undermine investor trust</strong> through opaque dealings.</li>
      </ul>
      <p>
        By mandating liquidity checks, approvals, and filings, regulators ensure such transactions are rare, transparent, and accountable.
      </p>
    </div>
  );
};

export default Whitewash;